import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

export interface PaginatedResponse {
  items: any[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export const API_ORIGIN = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
export const API_BASE_URL = API_ORIGIN.endsWith('/api') ? API_ORIGIN : `${API_ORIGIN}/api`;
const baseUrl = API_BASE_URL;

const rawBaseQuery = fetchBaseQuery({ baseUrl, credentials: 'include' });

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Try refreshing the token
    const refreshResult = await rawBaseQuery(
      { url: '/auth/refresh', method: 'POST' },
      api,
      extraOptions,
    );

    if (refreshResult.error) {
      // Refresh failed — force redirect to login
      window.dispatchEvent(new CustomEvent('auth:expired'));
      return result;
    }

    // Retry the original request with the new token
    result = await rawBaseQuery(args, api, extraOptions);
  }

  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Station', 'Channel', 'Program', 'Track', 'Notification', 'User'],
  endpoints: (builder) => ({
    login: builder.mutation<
      { message: string; role: string; stationId?: number | null; username: string },
      { username: string; password: string }
    >({
      query: (body) => ({
        url: '/auth/login',
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
    }),
    listStations: builder.query<any[], void>({
      query: () => '/stations',
      providesTags: ['Station'],
    }),
    getStationCredentials: builder.query<
      { stationId: number; username: string; role: string },
      number
    >({
      query: (stationId) => `/stations/${stationId}/credentials`,
      providesTags: ['Station'],
    }),
    createStation: builder.mutation<any, { name: string; code: string; status: string }>({
      query: (body) => ({
        url: '/stations',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Station'],
    }),
    updateStation: builder.mutation<any, { stationId: number; name?: string; status?: string }>({
      query: ({ stationId, ...body }) => ({
        url: `/stations/${stationId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Station'],
    }),
    deleteStation: builder.mutation<{ deleted: boolean; station_id: number }, number>({
      query: (stationId) => ({
        url: `/stations/${stationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Station'],
    }),
    updateStationCredentials: builder.mutation<
      { stationId: number; username: string; role: string; loginPassword?: string | null },
      { stationId: number; username?: string; password?: string; resetPassword?: boolean }
    >({
      query: ({ stationId, ...body }) => ({
        url: `/stations/${stationId}/credentials`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Station'],
    }),
    listChannels: builder.query<any[], number>({
      query: (stationId) => `/channels/stations/${stationId}`,
      providesTags: ['Channel'],
    }),
    createChannel: builder.mutation<any, { stationId: number; channel_number: number; name: string; status: string }>({
      query: ({ stationId, ...body }) => ({
        url: `/channels/stations/${stationId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Channel'],
    }),
    updateChannel: builder.mutation<any, { channelId: number; name?: string; status?: string }>({
      query: ({ channelId, ...body }) => ({
        url: `/channels/${channelId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Channel'],
    }),
    deleteChannel: builder.mutation<{ deleted: boolean; channel_id: number }, number>({
      query: (channelId) => ({
        url: `/channels/${channelId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Channel'],
    }),
    listPrograms: builder.query<any[], number>({
      query: (channelId) => `/programs/channels/${channelId}`,
      providesTags: ['Program'],
    }),
    getProgram: builder.query<any, number>({
      query: (programId) => `/programs/${programId}`,
      providesTags: ['Program'],
    }),
    createProgram: builder.mutation<any, { channelId: number; payload: Record<string, unknown> }>({
      query: ({ channelId, payload }) => ({
        url: `/programs/channels/${channelId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Program'],
    }),
    createRecurringProgram: builder.mutation<{ created: any[]; conflicts: Array<{ startDateTime: string; endDateTime: string }>; seriesId: string }, { channelId: number; payload: Record<string, unknown> }>({
      query: ({ channelId, payload }) => ({
        url: `/programs/channels/${channelId}/recurring`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Program'],
    }),
    updateProgram: builder.mutation<any, { programId: number; payload: Record<string, unknown> }>({
      query: ({ programId, payload }) => ({
        url: `/programs/${programId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['Program'],
    }),
    deleteProgram: builder.mutation<{ deleted: boolean }, { programId: number; mode?: 'single' | 'series' }>({
      query: ({ programId, mode }) => ({
        url: `/programs/${programId}${mode === 'series' ? '?mode=series' : ''}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Program'],
    }),
    listTracks: builder.query<any[], number>({
      query: (channelId) => `/tracks/channels/${channelId}`,
      providesTags: ['Track'],
    }),
    uploadTrack: builder.mutation<any, { channelId: number; file: File }>({
      query: ({ channelId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/tracks/channels/${channelId}`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Track'],
    }),
    deleteTrack: builder.mutation<{ deleted: boolean }, number>({
      query: (trackId) => ({
        url: `/tracks/${trackId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Track', 'Program'],
    }),
    uploadThumbnail: builder.mutation<{ url: string }, { channelId: number | string; file: File }>({
      query: ({ channelId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/programs/channels/${channelId}/thumbnail`,
          method: 'POST',
          body: formData,
        };
      },
    }),
    listNotifications: builder.query<
      { id: number; channelId: number; type: string; title: string; message: string | null; isRead: boolean; createdAt: string }[],
      number
    >({
      query: (channelId) => `/notifications/channels/${channelId}`,
      providesTags: (_r, _e, channelId) => [{ type: 'Notification', id: `channel-${channelId}` }],
    }),
    listStationNotifications: builder.query<
      { id: number; channelId: number; type: string; title: string; message: string | null; isRead: boolean; createdAt: string }[],
      number
    >({
      query: (stationId) => `/notifications/stations/${stationId}`,
      providesTags: (_r, _e, stationId) => [{ type: 'Notification', id: `station-${stationId}` }],
    }),
    markNotificationRead: builder.mutation<{ ok: boolean }, number>({
      query: (notificationId) => ({
        url: `/notifications/${notificationId}/read`,
        method: 'PUT',
      }),
      invalidatesTags: [{ type: 'Notification' }],
    }),
    markAllNotificationsRead: builder.mutation<{ ok: boolean }, number>({
      query: (channelId) => ({
        url: `/notifications/channels/${channelId}/read-all`,
        method: 'PUT',
      }),
      invalidatesTags: (_r, _e, channelId) => [{ type: 'Notification', id: `channel-${channelId}` }],
    }),
    markAllStationNotificationsRead: builder.mutation<{ ok: boolean }, number>({
      query: (stationId) => ({
        url: `/notifications/stations/${stationId}/read-all`,
        method: 'PUT',
      }),
      invalidatesTags: (_r, _e, stationId) => [{ type: 'Notification', id: `station-${stationId}` }],
    }),
    listAllNotifications: builder.query<
      { id: number; channelId: number; type: string; title: string; message: string | null; isRead: boolean; createdAt: string }[],
      void
    >({
      query: () => `/notifications/all`,
      providesTags: [{ type: 'Notification', id: 'all' }],
    }),
    markAllGlobalNotificationsRead: builder.mutation<{ ok: boolean }, void>({
      query: () => ({
        url: `/notifications/all/read-all`,
        method: 'PUT',
      }),
      invalidatesTags: [{ type: 'Notification', id: 'all' }],
    }),

    // ── Notification count endpoints (lightweight, for badge) ──

    notificationCountChannel: builder.query<
      { total: number; unread: number },
      number
    >({
      query: (channelId) => `/notifications/channels/${channelId}/count`,
      providesTags: (_r, _e, channelId) => [{ type: 'Notification', id: `channel-${channelId}` }],
    }),
    notificationCountStation: builder.query<
      { total: number; unread: number },
      number
    >({
      query: (stationId) => `/notifications/stations/${stationId}/count`,
      providesTags: (_r, _e, stationId) => [{ type: 'Notification', id: `station-${stationId}` }],
    }),
    notificationCountAll: builder.query<
      { total: number; unread: number },
      void
    >({
      query: () => `/notifications/all/count`,
      providesTags: [{ type: 'Notification', id: 'all' }],
    }),

    // ── Delete notification mutations ──

    deleteNotification: builder.mutation<{ ok: boolean }, number>({
      query: (notificationId) => ({
        url: `/notifications/${notificationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Notification' }],
    }),
    clearChannelNotifications: builder.mutation<{ ok: boolean }, number>({
      query: (channelId) => ({
        url: `/notifications/channels/${channelId}/clear`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, channelId) => [{ type: 'Notification', id: `channel-${channelId}` }],
    }),
    clearStationNotifications: builder.mutation<{ ok: boolean }, number>({
      query: (stationId) => ({
        url: `/notifications/stations/${stationId}/clear`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, stationId) => [{ type: 'Notification', id: `station-${stationId}` }],
    }),
    clearAllNotifications: builder.mutation<{ ok: boolean }, void>({
      query: () => ({
        url: `/notifications/all/clear`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Notification', id: 'all' }],
    }),

    // ── Paginated endpoints (server-side infinite scroll) ──

    listStationsPaginated: builder.query<
      PaginatedResponse,
      { offset: number; limit: number; search?: string }
    >({
      query: ({ offset, limit, search }) => {
        const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        if (search) params.set('search', search);
        return `/stations?${params}`;
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return `stations-${queryArgs.search ?? ''}`;
      },
      merge(currentCache, newItems, { arg }) {
        if (arg.offset === 0) return newItems;
        return { ...newItems, items: [...currentCache.items, ...newItems.items] };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: ['Station'],
    }),

    listChannelsPaginated: builder.query<
      PaginatedResponse,
      { stationId: number; offset: number; limit: number; search?: string }
    >({
      query: ({ stationId, offset, limit, search }) => {
        const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        if (search) params.set('search', search);
        return `/channels/stations/${stationId}?${params}`;
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return `channels-${queryArgs.stationId}-${queryArgs.search ?? ''}`;
      },
      merge(currentCache, newItems, { arg }) {
        if (arg.offset === 0) return newItems;
        return { ...newItems, items: [...currentCache.items, ...newItems.items] };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: ['Channel'],
    }),

    listProgramsPaginated: builder.query<
      PaginatedResponse,
      { channelId: number; offset: number; limit: number; search?: string }
    >({
      query: ({ channelId, offset, limit, search }) => {
        const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        if (search) params.set('search', search);
        return `/programs/channels/${channelId}?${params}`;
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return `programs-${queryArgs.channelId}-${queryArgs.search ?? ''}`;
      },
      merge(currentCache, newItems, { arg }) {
        if (arg.offset === 0) return newItems;
        return { ...newItems, items: [...currentCache.items, ...newItems.items] };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: ['Program'],
    }),

    listTracksPaginated: builder.query<
      PaginatedResponse,
      { channelId: number; offset: number; limit: number }
    >({
      query: ({ channelId, offset, limit }) =>
        `/tracks/channels/${channelId}?limit=${limit}&offset=${offset}`,
      serializeQueryArgs: ({ queryArgs }) => {
        return `tracks-${queryArgs.channelId}`;
      },
      merge(currentCache, newItems, { arg }) {
        if (arg.offset === 0) return newItems;
        return { ...newItems, items: [...currentCache.items, ...newItems.items] };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: ['Track'],
    }),

    // ── Paginated notification endpoints ──

    listNotificationsPaginated: builder.query<
      PaginatedResponse,
      { channelId: number; offset: number; limit: number }
    >({
      query: ({ channelId, offset, limit }) =>
        `/notifications/channels/${channelId}?limit=${limit}&offset=${offset}`,
      serializeQueryArgs: ({ queryArgs }) => {
        return `notif-channel-${queryArgs.channelId}`;
      },
      merge(currentCache, newItems, { arg }) {
        if (arg.offset === 0) return newItems;
        return { ...newItems, items: [...currentCache.items, ...newItems.items] };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: (_r, _e, { channelId }) => [{ type: 'Notification', id: `channel-${channelId}` }],
    }),

    listStationNotificationsPaginated: builder.query<
      PaginatedResponse,
      { stationId: number; offset: number; limit: number }
    >({
      query: ({ stationId, offset, limit }) =>
        `/notifications/stations/${stationId}?limit=${limit}&offset=${offset}`,
      serializeQueryArgs: ({ queryArgs }) => {
        return `notif-station-${queryArgs.stationId}`;
      },
      merge(currentCache, newItems, { arg }) {
        if (arg.offset === 0) return newItems;
        return { ...newItems, items: [...currentCache.items, ...newItems.items] };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: (_r, _e, { stationId }) => [{ type: 'Notification', id: `station-${stationId}` }],
    }),

    listAllNotificationsPaginated: builder.query<
      PaginatedResponse,
      { offset: number; limit: number }
    >({
      query: ({ offset, limit }) =>
        `/notifications/all?limit=${limit}&offset=${offset}`,
      serializeQueryArgs: () => {
        return `notif-all`;
      },
      merge(currentCache, newItems, { arg }) {
        if (arg.offset === 0) return newItems;
        return { ...newItems, items: [...currentCache.items, ...newItems.items] };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: [{ type: 'Notification', id: 'all' }],
    }),

    // ── User management endpoints ──

    listUsers: builder.query<
      { id: number; userid: string; isAssigned: boolean; assignedAt: string | null; createdAt: string; subscriptions: { id: number; stationId: number; stationName: string; expiryAt: string | null; createdAt: string }[] }[],
      string | void
    >({
      query: (search) => search ? `/users?search=${encodeURIComponent(search)}` : '/users',
      providesTags: ['User'],
    }),
    createUser: builder.mutation<
      { id: number; userid: string; isAssigned: boolean; assignedAt: string | null; createdAt: string; subscriptions: any[] },
      { userid: string }
    >({
      query: (body) => ({
        url: '/users',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    deleteUser: builder.mutation<{ deleted: boolean; userId: number }, number>({
      query: (userId) => ({
        url: `/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    assignStation: builder.mutation<any, { userId: number; stationId: number }>({
      query: ({ userId, stationId }) => ({
        url: `/users/${userId}/subscriptions`,
        method: 'POST',
        body: { stationId },
      }),
      invalidatesTags: ['User'],
    }),
    revokeStation: builder.mutation<{ deleted: boolean; subscriptionId: number }, { userId: number; subscriptionId: number }>({
      query: ({ userId, subscriptionId }) => ({
        url: `/users/${userId}/subscriptions/${subscriptionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useLoginMutation,
  useListStationsQuery,
  useGetStationCredentialsQuery,
  useCreateStationMutation,
  useUpdateStationMutation,
  useDeleteStationMutation,
  useUpdateStationCredentialsMutation,
  useListChannelsQuery,
  useCreateChannelMutation,
  useUpdateChannelMutation,
  useDeleteChannelMutation,
  useListProgramsQuery,
  useCreateProgramMutation,
  useCreateRecurringProgramMutation,
  useUpdateProgramMutation,
  useDeleteProgramMutation,
  useGetProgramQuery,
  useListTracksQuery,
  useUploadTrackMutation,
  useDeleteTrackMutation,
  useUploadThumbnailMutation,
  useListNotificationsQuery,
  useListStationNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useMarkAllStationNotificationsReadMutation,
  useListAllNotificationsQuery,
  useMarkAllGlobalNotificationsReadMutation,
  useListStationsPaginatedQuery,
  useListChannelsPaginatedQuery,
  useListProgramsPaginatedQuery,
  useListTracksPaginatedQuery,
  useListNotificationsPaginatedQuery,
  useListStationNotificationsPaginatedQuery,
  useListAllNotificationsPaginatedQuery,
  useDeleteNotificationMutation,
  useClearChannelNotificationsMutation,
  useClearStationNotificationsMutation,
  useClearAllNotificationsMutation,
  useNotificationCountChannelQuery,
  useNotificationCountStationQuery,
  useNotificationCountAllQuery,
  useListUsersQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
  useAssignStationMutation,
  useRevokeStationMutation,
} = api;
